import styled from "styled-components";
import chunk from "lodash/chunk";
import { VehicleType } from "../vehicles/types";
import { formatDate } from "../../utils/datetime";
import PdfPreview from "./components/PdfPreview";
import PdfPage from "./components/PdfPage";
import VehicleHeading from "./components/VehicleHeading";
import VehicleImages from "./components/VehicleImages";
import Header from "../../components/Header";
import VehicleInfo from "../vehicles/components/VehicleInfo";
import { isTelematicsField } from "../vehicles/fields";
import Get from "../../components/Get";
import { format } from "date-fns";
import { fieldToLabel } from "../vehicles/formConfig";

const S = {
	GroupWrapper: styled.div`
        margin-top: 60px;
        & > div:nth-child(2n) {
            background: #f5f5f5;
        }
    `,
	Header: styled.h1`
        margin: 16px 0 8px 0;
        padding-bottom: 4px;
        border-bottom: 1px solid black;
        font-size: 14px;
        font-weight: 700;
        text-transform: uppercase;
    `,
	DataLine: styled.div`
        display: grid;
        grid-template-columns: 1fr 2fr;
        padding: 4px 8px;
        min-width: 0;
        align-items: center;
        & > div {
            min-width: 0;
            font-size: 13px;
        }
    `,
	Note: styled.p`
        font-size: 14px !important;
    `,
	Locations: styled.div`
        font-size: 14px;
        & > div:nth-child(2n) {
            background: #f5f5f5;
        }
    `,
	LocationLine: styled.div`
        display: grid;
        align-items: center;
        grid-template-columns: 2fr 3fr;
        grid-gap: 16px;
        padding: 6px 0;
        & > p {
            min-width: 0;
        }
    `,
};

const vehicleDetailsFields = [
	{ key: "make", fallback: "unknown" },
	{ key: "model", fallback: "unknown" },
	{ key: "vinNumber", fallback: "unknown" },
	{ key: "dateCreated", fallback: "unknown", formatter: formatDate },
	{ key: "dateOfTheft", fallback: "unknown", formatter: formatDate },
];

const VehicleDetails = ({ vehicle }: { vehicle: VehicleType }) => (
	<S.GroupWrapper>
		<S.Header>Vehicle Details</S.Header>
		{vehicleDetailsFields.map(({ key, fallback, formatter }) => (
			<S.DataLine key={key}>
				<div>{fieldToLabel(key)}</div>
				<div>
					{formatter
						? formatter(vehicle[key]) || fallback
						: vehicle[key] || fallback}
				</div>
			</S.DataLine>
		))}
	</S.GroupWrapper>
);

interface ReportProps {
	vehicle: VehicleType;
}

const TelematicsReport = ({ vehicle }: ReportProps) => {
	const telematics = !vehicle.firstLocatedAt
		? ""
		: `The vehicle was first located on the ${format(new Date(vehicle.firstLocatedDate), "MMM dd, yyyy - HH:mm:ss") || "unkown date"} 
         at ${vehicle.firstLocatedAt || "unkown location"}.`;
	const body = `
            With reference to the vehicle bearing the Vehicle Identity Number (VIN) of
            ${vehicle.vinNumber || "unkown vin"}. Digitpol activated, based on a request from the ${vehicle.clientName || "client"} 
            and an active SIS warrant, a tracking system which enabled Digitpol to geolocate the 
            vehicle. ${telematics}
    `;
	const containsLongNote = (vehicle.note || "").length > 600;
	const vehicleInfo = (
		<VehicleInfo
			header="Telematics / GPS Data"
			vehicle={vehicle}
			fieldFilter={isTelematicsField}
			compact
		/>
	);
	return (
		<PdfPreview>
			<PdfPage>
				<VehicleHeading fileName="Telematics Report" vehicle={vehicle} />
				<br />
				<p>{body}</p>
				<VehicleImages
					vehicle={vehicle}
					header="Images"
					imageField="screenshots"
				/>
			</PdfPage>

			<PdfPage>
				<VehicleDetails vehicle={vehicle} />

				<br />
				{vehicle.note && <b>Intelligence</b>}
				<S.Note>{vehicle.note}</S.Note>

				<br />
				{!containsLongNote && vehicleInfo}
			</PdfPage>

			{containsLongNote && <PdfPage>{vehicleInfo}</PdfPage>}

			<Get
				url={`/vehicles/${vehicle.id}/all-locations/`}
				defaultErrorMessage="Not found"
				hideError
			>
				{({ data }) => {
					const pages = chunk(data, 37); // Limit 37 lines per page.
					return pages.map((page, i) => (
						<PdfPage
							key={
								page.length > 0
									? `${page[0].date}-${page[page.length - 1].date}`
									: i
							}
						>
							{i === 0 ? (
								<Header>Historic Telematics Coordinates</Header>
							) : (
								<div className="mt-4" />
							)}
							<S.Locations>
								{page.map((location) => (
									<S.LocationLine
										key={`${location.date}-${location.lat}-${location.long}`}
									>
										<p>
											Timestamp:{" "}
											{format(
												new Date(location.date),
												"MMM dd, yyyy - HH:mm:ss",
											)}
										</p>
										<p>
											GPS Coordinates: {location.lat} {location.long}
										</p>
									</S.LocationLine>
								))}
							</S.Locations>
						</PdfPage>
					));
				}}
			</Get>
		</PdfPreview>
	);
};

export default TelematicsReport;
